import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Search, Scale, Save, Wand2, X, Trash2 } from "lucide-react";
import { SubLinks } from "@/components/SubLinks";

export default function PriceCompare() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [onlyMulti, setOnlyMulti] = useState(false);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState({ vendor_id: "", price: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["compare", q, onlyMulti],
    queryFn: async () => (await api.get(`/procurement/compare?q=${encodeURIComponent(q)}&only_multi=${onlyMulti}`)).data,
  });
  const { data: vendors } = useQuery({
    queryKey: ["vendors"],
    queryFn: async () => (await api.get("/procurement/vendors")).data,
  });

  const savePrice = useMutation({
    mutationFn: async ({ item_id, vendor_id, price, unit }) =>
      (await api.put("/procurement/prices", { item_id, vendor_id, price: Number(price) || 0, unit })).data,
    onSuccess: () => {
      setDraft({ vendor_id: "", price: "" });
      qc.invalidateQueries({ queryKey: ["compare"] });
      toast.success("Η τιμή αποθηκεύτηκε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const applyBest = useMutation({
    mutationFn: async (item_id) => (await api.post(`/procurement/prices/apply-best/${item_id}`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["compare"] });
      qc.invalidateQueries({ queryKey: ["ingredients"] });
      qc.invalidateQueries({ queryKey: ["recipes"] });
      toast.success(`Ενημερώθηκε το κόστος: ${fmtEuroCents(d.price_per_unit)} (${d.supplier})`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const patchNotes = useMutation({
    mutationFn: async ({ id, notes }) => (await api.patch(`/recipes/ingredients/${id}`, { notes })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["compare"] }); qc.invalidateQueries({ queryKey: ["ingredients"] }); toast.success("Αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/recipes/ingredients/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["compare"] }); qc.invalidateQueries({ queryKey: ["ingredients"] }); toast.success("Το προϊόν διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const current = (data?.rows || []).find((x) => x.item_id === detail) || null;

  return (
    <div className="space-y-6" data-testid="compare-page">
      <SubLinks links={[{ to: "/bidding", label: "Διαγωνισμός Προμηθευτών" }, { to: "/orders", label: "Παραγγελίες" }]} />
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Προμήθειες</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Σύγκριση Τιμών</h1>
        <p className="text-[#6F6A60] text-sm mt-1">Όλες οι προσφορές ανά είδος, αναγμένες στην ίδια μονάδα. Με ένα κλικ περνάς την καλύτερη τιμή στο κόστος συνταγών.</p>
        <p className="text-xs mt-1"><span className="text-[#4A7A3E] font-semibold">● Πράσινο</span> = καλύτερη τιμή ≤ στόχου · <span className="text-[#991B1B] font-semibold">● Κόκκινο</span> = πάνω από τον στόχο</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="cmp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση είδους"
            className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C]" />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-[#45423C]">
          <input data-testid="cmp-only-multi" type="checkbox" checked={onlyMulti} onChange={(e) => setOnlyMulti(e.target.checked)} />
          Μόνο είδη με 2+ προσφορές
        </label>
        <span className="text-sm text-[#6F6A60] ml-auto">{data?.count || 0} είδη</span>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <Scale className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">Είδη & προσφορές</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">Είδος</th>
                <th className="px-3 py-3">Περιγραφή</th>
                <th className="px-3 py-3 text-center">Μονάδα</th>
                <th className="px-3 py-3 text-right">Τρέχον κόστος</th>
                <th className="px-3 py-3 text-right">Καλύτερη τιμή</th>
                <th className="px-3 py-3 text-right">Στόχος</th>
                <th className="px-3 py-3">Φθηνότερος</th>
                <th className="px-3 py-3 text-center">Προσφορές</th>
                <th className="px-3 py-3 text-right">Διασπορά</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {(data?.rows || []).map((r) => (
                <tr key={r.item_id} data-testid={`cmp-row-${r.item_id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2.5 font-medium text-[#45423C]">
                    <button data-testid={`cmp-open-${r.item_id}`} onClick={() => setDetail(r.item_id)} className="hover:underline">{r.item_name}</button>
                  </td>
                  <td className="px-3 py-2.5">
                    <input data-testid={`cmp-notes-${r.item_id}`} defaultValue={r.notes || ""} placeholder="—"
                      onBlur={(e) => { if ((e.target.value || "") !== (r.notes || "")) patchNotes.mutate({ id: r.item_id, notes: e.target.value }); }}
                      className="w-full min-w-[150px] rounded-lg border border-transparent hover:border-[#E3DED4] focus:border-[#45423C] px-2 py-1 text-sm outline-none bg-transparent" />
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs">{r.unit}</td>
                  <td className="px-3 py-2.5 text-right tabular">{fmtEuroCents(r.current_price)}</td>
                  <td className={`px-3 py-2.5 text-right tabular font-semibold ${r.price_target && r.best_price !== null ? (r.best_price <= r.price_target ? "text-[#4A7A3E]" : "text-[#991B1B]") : ""}`}>
                    {r.best_price === null ? "—" : fmtEuroCents(r.best_price)}
                    {r.price_target && r.best_price !== null ? (r.best_price <= r.price_target ? " ▼" : " ▲") : ""}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular text-[#6F6A60]">{r.price_target ? fmtEuroCents(r.price_target) : "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-[#6F6A60]">{r.best_vendor || "—"}</td>
                  <td className="px-3 py-2.5 text-center tabular">{r.offer_count}</td>
                  <td className={`px-3 py-2.5 text-right tabular ${r.spread_pct > 15 ? "text-[#991B1B] font-semibold" : "text-[#6F6A60]"}`}>
                    {r.offer_count > 1 ? `${r.spread_pct}%` : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {r.best_price !== null && Math.abs(r.best_price - r.current_price) > 0.001 && (
                      <button data-testid={`cmp-apply-${r.item_id}`} onClick={() => applyBest.mutate(r.item_id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#C86235] hover:underline">
                        <Wand2 className="h-3.5 w-3.5" /> Εφαρμογή
                      </button>
                    )}
                    <button data-testid={`cmp-del-${r.item_id}`} onClick={() => { if (window.confirm(`Διαγραφή του «${r.item_name}»;`)) del.mutate(r.item_id); }}
                      className="ml-2 p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B] align-middle"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
              {!isLoading && (data?.rows || []).length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-[#6F6A60]">Δεν βρέθηκαν είδη.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {current && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="cmp-modal">
          <div className="bg-white rounded-2xl w-full max-w-xl mt-12 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <h3 className="font-medium text-[#45423C]">{current.item_name} · ανά {current.unit}</h3>
              <button data-testid="cmp-close" onClick={() => setDetail(null)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#6F6A60] bg-[#F1EDE6]">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Προμηθευτής</th>
                    <th className="px-3 py-2 text-right">Τιμή</th>
                    <th className="px-3 py-2 text-right">Διαφορά</th>
                    <th className="px-3 py-2">Συσκευασία</th>
                  </tr>
                </thead>
                <tbody>
                  {current.offers.map((o) => (
                    <tr key={o.id} data-testid={`cmp-offer-${o.id}`} className={`border-t border-[#E3DED4] ${o.rank === 1 ? "bg-[#F1F4EE]" : ""}`}>
                      <td className="px-3 py-2 tabular">{o.rank}</td>
                      <td className="px-3 py-2">{o.vendor_name}</td>
                      <td className={`px-3 py-2 text-right tabular font-semibold ${current.price_target ? (o.unit_price <= current.price_target ? "text-[#4A7A3E]" : "text-[#991B1B]") : ""}`}>{fmtEuroCents(o.unit_price)}</td>
                      <td className={`px-3 py-2 text-right tabular ${o.diff_pct > 0 ? "text-[#991B1B]" : "text-[#6E7A63]"}`}>{o.diff_pct ? `+${o.diff_pct}%` : "καλύτερη"}</td>
                      <td className="px-3 py-2 text-xs text-[#6F6A60]">{o.pack_desc || "—"}</td>
                    </tr>
                  ))}
                  {current.offers.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-[#6F6A60]">Καμία προσφορά ακόμη.</td></tr>}
                </tbody>
              </table>

              <div className="rounded-xl bg-[#F7F5F1] p-4 space-y-2">
                <h4 className="text-[10px] uppercase tracking-widest text-[#6F6A60] font-bold">Καταχώρηση τιμής</h4>
                <div className="flex flex-wrap gap-2">
                  <select data-testid="cmp-vendor" value={draft.vendor_id} onChange={(e) => setDraft({ ...draft, vendor_id: e.target.value })}
                    className="flex-1 min-w-[180px] rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]">
                    <option value="">— προμηθευτής —</option>
                    {(vendors?.rows || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  <input data-testid="cmp-price" type="number" step="0.01" value={draft.price} placeholder={`€ / ${current.unit}`}
                    onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                    className="w-32 rounded-xl border border-[#E3DED4] px-3 py-2 text-right text-sm tabular outline-none focus:border-[#45423C]" />
                  <button data-testid="cmp-save-price" disabled={!draft.vendor_id || !draft.price || savePrice.isPending}
                    onClick={() => savePrice.mutate({ item_id: current.item_id, vendor_id: draft.vendor_id, price: draft.price, unit: current.unit })}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
                    <Save className="h-4 w-4" /> Αποθήκευση
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
